import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { StudyShell } from './StudyShell';
import { getStudyHome, STUDY_HOME_QUERY_KEY, type StudyTerm } from './studyApi';
import { StudyJourneyRecords } from './StudyJourneyRecords';
import { StudyYearStory } from './StudyJourneyPortfolio';
import { useJourneyStory } from './useJourneyStory';
import { DEFAULT_STUDY_TIMEZONE } from './studyTermProgress';
import { getStudyJourney } from './studyYearApi';
import { STUDY_WRAP_UP_PATH } from './studyYearRoutes';

/** The Term My journey opens on: the current Term, else the latest Term that has started. */
function defaultTerm(terms: StudyTerm[]): StudyTerm | undefined {
  return (
    terms.find((term) => term.is_current) ??
    [...terms].reverse().find((term) => term.status === 'finished') ??
    terms[0]
  );
}

const pill = (selected: boolean) =>
  clsx(
    'min-h-11 rounded-full border px-4 text-sm font-bold transition-colors',
    selected
      ? 'border-ink bg-wash-sky text-ink'
      : 'border-hairline bg-canvas-pure text-slate2 hover:bg-surface hover:text-ink',
  );

/**
 * My journey (study-my-year-prd.md §6): what the learner did, Term by Term, and their year story.
 * A record, not a score: no mastery, no help levels, no filters beyond year and Term.
 */
export function StudyJourneyPage() {
  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const home = useQuery({ queryKey: STUDY_HOME_QUERY_KEY, queryFn: getStudyHome, retry: false });
  const years = home.data?.academic_years ?? [];
  const yearId = params.get('year') ?? home.data?.academic_year?.id ?? '';
  const viewedYear = years.find((year) => year.id === yearId);
  const terms = viewedYear?.terms ?? [];
  const termId = params.get('term') ?? defaultTerm(terms)?.id ?? '';
  const term = terms.find((row) => row.id === termId);
  const story = useJourneyStory(yearId);
  const journey = useInfiniteQuery({
    queryKey: ['study-journey', yearId, termId],
    queryFn: ({ pageParam }) =>
      getStudyJourney({ academic_year_id: yearId, term_id: termId, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(yearId && termId),
    retry: false,
  });
  const first = journey.data?.pages[0];
  const items = journey.data?.pages.flatMap((page) => page.items) ?? [];
  const timezone = first?.academic_year.timezone ?? DEFAULT_STUDY_TIMEZONE;
  const subjects = new Map(
    (first?.terms ?? terms).flatMap((row) =>
      (row.subjects ?? []).map((s) => [s.subject_key, s.display_name] as const),
    ),
  );
  const select = (patch: { year?: string; term?: string }) => {
    const next = new URLSearchParams(params);
    if (patch.year) {
      next.set('year', patch.year);
      next.delete('term');
    }
    if (patch.term) next.set('term', patch.term);
    setParams(next, { replace: true });
  };
  const returnToWrapUp = params.get('return') === 'wrap-up';

  return (
    <StudyShell>
      <header className="study-page-intro">
        <h1 className="text-3xl font-extrabold text-ink sm:text-4xl">My journey</h1>
        <p className="mt-2 text-ink-soft">
          What you did, Term by Term. This is a record, not a score.
        </p>
      </header>

      {home.isLoading && <p role="status" className="mt-6">Loading your journey…</p>}
      {home.isError && (
        <div role="alert" className="mt-6 rounded-3xl border border-hairline p-5">
          <p>This part didn&apos;t load. Your work is still saved.</p>
          <button type="button" className="btn-pill-ghost mt-3" onClick={() => void home.refetch()}>
            Try again
          </button>
        </div>
      )}
      {home.data && !home.data.academic_year && (
        <div className="mt-6 rounded-3xl bg-surface p-6">
          <p className="font-bold text-ink">Your journey starts when you set up this Term.</p>
          <Link className="btn-pill-primary mt-4 inline-flex" to="/learn/study?setup=term">
            Set up this Term
          </Link>
        </div>
      )}

      {home.data?.academic_year && (
        <>
          {returnToWrapUp && (
            <Link
              to={STUDY_WRAP_UP_PATH}
              className="mt-4 inline-flex min-h-11 items-center text-sm font-extrabold text-brand-sky underline underline-offset-4"
            >
              Back to wrap-up
            </Link>
          )}
          {years.length > 1 && (
            <div role="group" aria-label="School year" className="mt-6 flex flex-wrap gap-2">
              {years.map((year) => (
                <button
                  key={year.id}
                  type="button"
                  aria-pressed={year.id === yearId}
                  onClick={() => select({ year: year.id })}
                  className={pill(year.id === yearId)}
                >
                  {year.school_year}
                </button>
              ))}
            </div>
          )}
          {viewedYear && !viewedYear.is_current && (
            <p className="mt-4 rounded-2xl bg-surface p-4 text-sm text-ink">
              You&apos;re looking at {viewedYear.school_year}. Your current Term is not changed.
            </p>
          )}
          <div role="group" aria-label="Term" className="mt-4 flex flex-wrap gap-2">
            {terms.map((row) => (
              <button
                key={row.id}
                type="button"
                aria-pressed={row.id === termId}
                onClick={() => select({ term: row.id })}
                className={pill(row.id === termId)}
              >
                {`Term ${row.term_number}`}
              </button>
            ))}
          </div>

          <section aria-label="Records" className="mt-2">
            {journey.isLoading && <p role="status" className="mt-6">Loading your records…</p>}
            {journey.isError && (
              <div role="alert" className="mt-6 rounded-3xl border border-hairline p-5">
                <p>This part didn&apos;t load. Your work is still saved.</p>
                <button
                  type="button"
                  className="btn-pill-ghost mt-3"
                  onClick={() => void journey.refetch()}
                >
                  Try again
                </button>
              </div>
            )}
            {journey.data && items.length === 0 && (
              <p className="mt-6 rounded-3xl bg-surface p-6 text-ink">
                No records from this Term yet.
              </p>
            )}
            {items.length > 0 && (
              <StudyJourneyRecords
                key={`${yearId}:${termId}`}
                scopeKey={`${yearId}:${termId}`}
                items={items}
                timezone={timezone}
                termStartsOn={term?.starts_on ?? null}
                subjects={subjects}
                story={story}
                focusReflection={hash === '#reflection'}
              />
            )}
            {journey.hasNextPage && (
              <button
                type="button"
                className="btn-pill-ghost mt-5"
                disabled={journey.isFetchingNextPage}
                onClick={() => void journey.fetchNextPage()}
              >
                {journey.isFetchingNextPage ? 'Loading…' : 'Show earlier activities'}
              </button>
            )}
          </section>

          <StudyYearStory
            key={yearId}
            story={story}
            yearId={yearId}
            defaultOpen={hash === '#story'}
            onSaved={returnToWrapUp ? () => navigate(STUDY_WRAP_UP_PATH) : undefined}
          />
        </>
      )}
    </StudyShell>
  );
}
